import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Container, NbButton, NbCard } from "@/components/nb";
import { api } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function launchGoogle() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Auth() {
  const { login, signup } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", grade: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await signup(form.name, form.email, form.password, form.grade);
      toast.success("Welcome to EduMarket");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auth failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="max-w-lg">
      <NbCard color="white" className="p-8">
        <div className="flex gap-2 mb-6">
          <button onClick={() => setMode("login")} data-testid="tab-login" className={`px-4 py-2 rounded-lg nb-border font-heading font-bold uppercase text-xs ${mode === "login" ? "bg-sl-primary nb-shadow" : "bg-white"}`}>Log in</button>
          <button onClick={() => setMode("signup")} data-testid="tab-signup" className={`px-4 py-2 rounded-lg nb-border font-heading font-bold uppercase text-xs ${mode === "signup" ? "bg-sl-primary nb-shadow" : "bg-white"}`}>Sign up</button>
        </div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">{mode === "login" ? "Welcome back." : "Start your journey."}</h1>

        <button onClick={launchGoogle} data-testid="google-btn" className="mt-6 w-full flex items-center justify-center gap-3 px-5 py-3 rounded-lg nb-border nb-shadow nb-hover bg-white font-heading font-bold uppercase text-sm">
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.9 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.7 39.7 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.4-4.5 5.8l6.2 5.2C40.4 36.5 44 30.9 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-[2px] bg-black" />
          <span className="font-mono-sl text-[10px] uppercase text-neutral-600">or use email</span>
          <div className="flex-1 h-[2px] bg-black" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "signup" && (
            <>
              <input required data-testid="input-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 nb-border rounded-lg bg-white" />
              <input data-testid="input-grade" placeholder="Grade or year (e.g., 10, College Y1)" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="w-full px-4 py-3 nb-border rounded-lg bg-white" />
            </>
          )}
          <input required data-testid="input-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 nb-border rounded-lg bg-white" />
          <input required data-testid="input-password" type="password" placeholder="Password (min 6)" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-4 py-3 nb-border rounded-lg bg-white" />

          {mode === "login" && (
            <div className="text-right">
              <Link to="/auth/forgot" data-testid="forgot-link" className="font-mono-sl text-xs uppercase underline">Forgot password?</Link>
            </div>
          )}

          <NbButton type="submit" color="black" disabled={loading} data-testid="submit-auth" className="w-full">
            {loading ? "..." : mode === "login" ? "Log in" : "Create account"}
          </NbButton>
        </form>
      </NbCard>
    </Container>
  );
}
